import { useState } from "react";
import { api } from "@/lib/api";
import { X, Loader2, Quote, Trash2, Save, Sparkles } from "lucide-react";

const SUGGESTIONS = [
  "Easiest close I've ever done — wired the day after I saw the deal.",
  "Numbers were rock-solid. Showed up to the closing table with no surprises.",
  "Saved me a 3-month MLS search. Off-market gold.",
];

export default function TestimonialModal({ leadId, initial, sourceEventId, defaultAttribution, onClose, onSaved }) {
  const [quote, setQuote] = useState(initial?.quote || "");
  const [attribution, setAttribution] = useState(initial?.attribution || defaultAttribution || "");
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial?.quote;

  const save = async (clear = false) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/leads/${leadId}/testimonial`, {
        quote: clear ? "" : quote,
        attribution: clear ? "" : attribution,
        source_event_id: sourceEventId ?? initial?.source_event_id ?? null,
      });
      onSaved?.(data.testimonial);
      onClose?.();
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm flex items-center justify-center p-4 print:hidden"
      role="dialog"
      aria-modal="true"
      data-testid="testimonial-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between p-6 border-b border-[var(--line)]">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "#10B98122", color: "#10B981" }}>
              <Quote size={20} />
            </div>
            <div>
              <div className="label-eyebrow">{isEdit ? "Edit testimonial" : "Use as testimonial"}</div>
              <h2 className="font-display font-extrabold text-xl mt-1">Pull-quote for your Wins page</h2>
              <p className="text-xs text-[var(--ink-3)] mt-1">
                Polished quote + first-name attribution. No PII. Shows under the deal card on /wins/{`{token}`}.
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" aria-label="Close" data-testid="testimonial-modal-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <label className="block">
            <span className="label-eyebrow block mb-1.5">Quote</span>
            <textarea
              rows={3}
              className="form-input resize-none"
              maxLength={320}
              placeholder="e.g. Easiest close I've ever done — wired the day after I saw the deal."
              value={quote}
              onChange={(e) => setQuote(e.target.value)}
              data-testid="testimonial-quote-input"
              autoFocus
            />
            <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] mt-1 text-right">{quote.length}/320</div>
          </label>

          <div>
            <div className="label-eyebrow flex items-center gap-1.5 mb-2"><Sparkles size={11} /> Quick suggestions</div>
            <div className="flex flex-wrap gap-1.5">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setQuote(s)}
                  className="chip-button"
                  style={{ fontSize: 11, padding: "5px 10px", background: "rgba(15,23,42,0.04)" }}
                  data-testid="testimonial-suggestion"
                >
                  {s.slice(0, 50)}…
                </button>
              ))}
            </div>
          </div>

          <label className="block">
            <span className="label-eyebrow block mb-1.5">Attribution (first name + role)</span>
            <input
              type="text"
              className="form-input"
              maxLength={80}
              placeholder="e.g. Maya · cash buyer"
              value={attribution}
              onChange={(e) => setAttribution(e.target.value)}
              data-testid="testimonial-attribution-input"
            />
            <div className="text-xs text-[var(--ink-3)] mt-1">
              Stay first-name + role only. Never include phone, email, or last name.
            </div>
          </label>

          <div className="flex items-center gap-3 pt-3 border-t border-[var(--line)]">
            <button
              type="button"
              className="btn-money"
              onClick={() => save(false)}
              disabled={busy || !quote.trim()}
              style={{ background: "#10B981", borderColor: "#10B981" }}
              data-testid="testimonial-save-button"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {busy ? "Saving…" : isEdit ? "Update testimonial" : "Save testimonial"}
            </button>
            {isEdit && (
              <button
                type="button"
                className="btn-outline text-[var(--warn)]"
                onClick={() => save(true)}
                disabled={busy}
                data-testid="testimonial-clear-button"
              >
                <Trash2 size={13} /> Remove
              </button>
            )}
            <button type="button" className="btn-outline ml-auto" onClick={onClose} data-testid="testimonial-cancel-button">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
